import type { AppMessages } from "@/i18n/ja";
import { formatOHovRecordDurationMs } from "@/features/hoverIdle/oHov/oHovTimeFormat";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { compareHuge, type HugeNumber } from "@/shared/numbers";
import type { StatsSnapshot } from "./statsSnapshot";

export interface StatSection {
  readonly id: string;
  readonly getLabel: (messages: AppMessages) => string;
  readonly entries: readonly StatEntry[];
}

export interface StatEntry {
  readonly id: string;
  readonly getLabel: (messages: AppMessages) => string;
  readonly getValue: (
    stats: StatsSnapshot,
    messages: AppMessages,
  ) => string | null;
}

function formatHov(value: HugeNumber, stats: StatsSnapshot): string {
  return formatForContext(value, "compact", stats.notation);
}

function formatTim(value: HugeNumber, stats: StatsSnapshot): string {
  return formatForContext(value, "compact", stats.notation);
}

function formatOHovPower(value: HugeNumber, stats: StatsSnapshot): string {
  return formatForContext(value, "compact", stats.notation);
}

function formatHovCore(value: HugeNumber, stats: StatsSnapshot): string {
  return `${formatForContext(value, "main", stats.notation)} HC`;
}

function formatStatsDurationMs(valueMs: number, messages: AppMessages): string {
  if (!Number.isFinite(valueMs) || valueMs < 0) {
    return messages.records.stats.notAvailable;
  }
  return messages.records.stats.formatPlaytime(valueMs / 1000);
}

function formatFastestOHovDurationMs(
  valueMs: number,
  messages: AppMessages,
): string {
  return (
    formatOHovRecordDurationMs(
      valueMs,
      messages.common.formatMilliseconds,
      (durationMs) => formatStatsDurationMs(durationMs, messages),
    ) ?? messages.records.stats.notAvailable
  );
}

export const statsSections: readonly StatSection[] = [
  {
    id: "playtime",
    getLabel: (m) => m.records.stats.sectionPlaytime,
    entries: [
      {
        id: "onlinePlaytime",
        getLabel: (m) => m.records.stats.onlinePlaytime,
        getValue: (stats, m) =>
          m.records.stats.formatPlaytime(stats.onlinePlaytimeSeconds),
      },
      {
        id: "totalPlaytime",
        getLabel: (m) => m.records.stats.totalPlaytime,
        getValue: (stats, m) =>
          stats.totalPlaytimeSeconds !== null
            ? m.records.stats.formatPlaytime(stats.totalPlaytimeSeconds)
            : m.records.stats.notAvailable,
      },
    ],
  },
  {
    id: "hov",
    getLabel: (m) => m.records.stats.sectionHov,
    entries: [
      {
        id: "currentHov",
        getLabel: (m) => m.records.stats.currentHov,
        getValue: (stats) => formatHov(stats.currentHov, stats),
      },
      {
        id: "maxHov",
        getLabel: (m) => m.records.stats.maxHov,
        getValue: (stats) => formatHov(stats.maxHov, stats),
      },
      {
        id: "oHovPower",
        getLabel: (m) => m.records.stats.oHovPower,
        getValue: (stats) =>
          compareHuge(stats.oHovPower, 0) > 0
            ? formatOHovPower(stats.oHovPower, stats)
            : null,
      },
      {
        id: "oHovFastest",
        getLabel: (m) => m.records.stats.oHovFastest,
        getValue: (stats, m) => {
          if (!stats.isOHovUnlocked) return null;
          return stats.oHovFastestRunGameDurationMs === null
            ? m.records.stats.notAvailable
            : formatFastestOHovDurationMs(
                stats.oHovFastestRunGameDurationMs,
                m,
              );
        },
      },
      {
        id: "oHovCurrentTime",
        getLabel: (m) => m.records.stats.oHovCurrentTime,
        getValue: (stats, m) =>
          stats.isOHovUnlocked
            ? formatStatsDurationMs(stats.oHovCurrentRunGameElapsedMs, m)
            : null,
      },
      {
        id: "totalHovCoreSpent",
        getLabel: (m) => m.records.stats.totalHovCoreSpent,
        getValue: (stats) =>
          compareHuge(stats.totalHovCoreSpent, 0) > 0
            ? formatHovCore(stats.totalHovCoreSpent, stats)
            : null,
      },
    ],
  },
  {
    id: "tim",
    getLabel: (m) => m.records.stats.sectionTim,
    entries: [
      {
        id: "totalTimEarned",
        getLabel: (m) => m.records.stats.totalTimEarned,
        getValue: (stats) => formatTim(stats.totalTimEarned, stats),
      },
    ],
  },
  {
    id: "achievements",
    getLabel: (m) => m.records.stats.sectionAchievements,
    entries: [
      {
        id: "normalAchievements",
        getLabel: (m) => m.records.stats.normalAchievements,
        getValue: (stats) =>
          `${stats.normalAchievementUnlocked} / ${stats.normalAchievementTotal}`,
      },
      {
        id: "secretAchievements",
        getLabel: (m) => m.records.stats.secretAchievements,
        getValue: (stats) => String(stats.secretAchievementUnlocked),
      },
    ],
  },
];
